import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpRequest,
  HttpHandlerFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NgZone } from '@angular/core';
import {
  catchError,
  throwError,
  switchMap,
  from,
  Observable,
  Subject,
  filter,
  take,
} from 'rxjs';
import { AppSettings } from '../utils/app.settings';
import { SsoState } from '../../store/sso/sso.state';
import {
  SsoRefreshTokenAction,
  SsoClearStateAction,
} from '../../store/sso/sso.actions';
import { EnovaAlertsComponent } from '../utils/enova-alerts/enova-alerts.component';

let isRefreshing = false;
let refreshTokenSubject: Subject<string | null> = new Subject<string | null>();

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const store = inject(Store);
  const appSettings = inject(AppSettings);
  const dialog = inject(MatDialog);
  const router = inject(Router);
  const ngZone = inject(NgZone);

  // Add cache control to all requests
  req = req.clone({
    setHeaders: { 'Cache-Control': 'Max-Age=0' },
  });

  // Add authorization header if token exists
  const token = store.selectSnapshot(SsoState.token);

  if (token) {
    req = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !isExcludedFrom401(req.url, appSettings)) {
        return handleUnauthorized(
          req,
          next,
          store,
          appSettings,
          dialog,
          router,
          ngZone
        );
      }

      return throwError(() => error);
    })
  );
};

function isExcludedFrom401(url: string, appSettings: AppSettings): boolean {
  const excludedPaths = [
    appSettings.sso.urls.postVerifyToken,
    appSettings.sso.urls.refresh,
    appSettings.sso.urls.logout,
  ];

  const isExcluded = excludedPaths.some(path => url.includes(path));

  return isExcluded;
}

function handleUnauthorized(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  store: Store,
  appSettings: AppSettings,
  dialog: MatDialog,
  router: Router,
  ngZone: NgZone
): Observable<any> {
  const refreshToken = store.selectSnapshot(SsoState.refreshToken);

  if (!refreshToken) {
    handleLogout(store, dialog, router, ngZone);
    return throwError(() => new Error('No refresh token available'));
  }

  // Si ya se está refreshing, esperar al resultado del Subject
  if (isRefreshing) {
    return refreshTokenSubject.pipe(
      take(1), // Tomar solo el primer resultado que llegue
      switchMap(newToken => {
        if (newToken) {
          // Reintentar la petición original con el nuevo token
          const authenticatedReq = req.clone({
            setHeaders: { Authorization: `Bearer ${newToken}` },
          });
          return next(authenticatedReq);
        } else {
          handleLogout(store, dialog, router, ngZone);
          return throwError(() => new Error('Failed to refresh token'));
        }
      }),
      catchError(error => {
        // Solo hacer logout si es un error de autenticación, no errores de API normales
        if (error.status === 401) {
          handleLogout(store, dialog, router, ngZone);
        }
        return throwError(() => error);
      })
    );
  }

  // Iniciar proceso de refresh
  isRefreshing = true;
  return from(performTokenRefresh(refreshToken, store)).pipe(
    switchMap(newToken => {
      // Notificar a todas las peticiones en cola el nuevo token
      refreshTokenSubject.next(newToken);

      const authenticatedReq = req.clone({
        setHeaders: { Authorization: `Bearer ${newToken}` },
      });
      return next(authenticatedReq);
    }),
    catchError(error => {
      // Notificar a todas las peticiones en cola que falló
      refreshTokenSubject.next(null);

      handleLogout(store, dialog, router, ngZone);
      return throwError(() => error);
    })
  );
}

async function performTokenRefresh(
  refreshToken: string,
  store: Store
): Promise<string> {
  try {
    console.log('🔄 [AuthInterceptor] Iniciando refresh por 401...');

    // Dispatch refresh action y esperar a que complete
    await store.dispatch(new SsoRefreshTokenAction(refreshToken)).toPromise();
    // Verificar inmediatamente el estado después del dispatch
    let newToken = store.selectSnapshot(SsoState.token);
    const isAuthenticated = store.selectSnapshot(SsoState.isAuthenticated);

    // Si el token ya está disponible, retornarlo inmediatamente
    if (newToken && isAuthenticated) {
      console.log('✅ [AuthInterceptor] Token refrescado inmediatamente', {
        newTokenPrefix: newToken.substring(0, 20),
      });
      isRefreshing = false;

      // Verificar localStorage
      const tokenToVerify = newToken;
      setTimeout(() => verifyLocalStorageSync(store, tokenToVerify), 100);

      return newToken;
    }

    // Si no está disponible inmediatamente, esperar con polling mejorado
    let attempts = 0;
    const maxWaitAttempts = 100; // Aumentado a 10 segundos
    const waitInterval = 100;

    while (attempts < maxWaitAttempts) {
      await new Promise(resolve => setTimeout(resolve, waitInterval));

      newToken = store.selectSnapshot(SsoState.token);
      const currentIsAuthenticated = store.selectSnapshot(
        SsoState.isAuthenticated
      );

      // Verificar que tengamos un token válido y esté autenticado
      if (newToken && currentIsAuthenticated) {
        console.log(
          `✅ [AuthInterceptor] Token refrescado después de ${attempts * waitInterval}ms`,
          {
            newTokenPrefix: newToken.substring(0, 20),
          }
        );
        isRefreshing = false;

        // Verificar localStorage
        const tokenToVerify = newToken;
        setTimeout(() => verifyLocalStorageSync(store, tokenToVerify), 100);

        return newToken;
      }

      attempts++;
    }

    throw new Error('Timeout waiting for new token');
  } catch (error) {
    console.error('❌ [AuthInterceptor] Error en refresh:', error);
    isRefreshing = false;
    throw error;
  }
}

function verifyLocalStorageSync(store: Store, expectedToken: string): void {
  try {
    const storedState = localStorage.getItem('@@STATE');
    if (storedState) {
      const state = JSON.parse(storedState);

      if (state.sso?.token === expectedToken) {
        console.log(
          '✅ [AuthInterceptor] Token sincronizado correctamente en localStorage'
        );
      } else {
        console.warn(
          '⚠️ [AuthInterceptor] Token en localStorage no coincide con store:',
          {
            expectedTokenPrefix: expectedToken.substring(0, 20),
            localStorageTokenPrefix: state.sso?.token
              ? state.sso.token.substring(0, 20)
              : 'null',
          }
        );
      }
    } else {
      console.warn(
        '⚠️ [AuthInterceptor] No se encontró @@STATE en localStorage'
      );
    }
  } catch (error) {
    console.error('❌ [AuthInterceptor] Error verificando localStorage:', error);
  }
}

function handleLogout(
  store: Store,
  dialog: MatDialog,
  router: Router,
  ngZone: NgZone
): void {
  // Reset refresh state
  isRefreshing = false;
  refreshTokenSubject = new Subject<string | null>(); // Recrear el Subject para limpiar suscripciones

  // Clear application state
  store.dispatch(new SsoClearStateAction());

  // Show error dialog
  dialog.open(EnovaAlertsComponent, {
    data: {
      title: 'Error',
      text: 'Su sesión ha expirado. Por favor, inicie sesión nuevamente.',
      type: 'error',
      hiddeCancelBtn: true,
    },
  });

  // Navigate to login
  ngZone.run(() => {
    router.navigate(['/']);
  });
}
